/* Help topics, shared by the public /help page and the in-app Help panel.
   Each topic: {id, title, code, body (trusted HTML, written here), keywords (extra search words)}.
   Keep the wording plain British English, short sentences, and true to what the app does today.
   Facts come from docs/guide.html and the app itself; update both when a feature changes.
   THIS FILE IS THE SOURCE OF TRUTH: work-rolodex.html and app/src/app.js each keep an inline copy
   of this same HELP_TOPICS array (they can't import a module) — when you change a topic here, copy
   the change into both of those too. Search them for "HELP_TOPICS" to find the copies. */
export var HELP_TOPICS = [
  {
    id: "getting-started",
    code: "GO",
    title: "Getting started",
    keywords: "start begin first sign in google account workspace new sample data welcome",
    body:
      "<p>Tabwheel keeps your work on cards, like an old desk rolodex. Each customer, job, quote, supplier or meeting gets its own card.</p>" +
      "<ol>" +
        "<li>Sign in with your Google account. You get your own private workspace.</li>" +
        "<li>Click <b>+ New card</b> at the top and pick a type. Give it a name and fill in what you know.</li>" +
        "<li>Spin the wheel to find cards. Click the front card to open it and write notes.</li>" +
        "<li>Check <b>Today</b> each morning for what's overdue, due today and coming up.</li>" +
      "</ol>" +
      "<p>Setting up a brand-new workspace? Pick a starter set (Trades &amp; services, Office &amp; agency, Shop &amp; hospitality, Property &amp; lettings, or IT &amp; infrastructure) and it adds matching card types and a few example cards — or choose <b>Start blank</b>. You can add another starter set, or hide types you don't use, later from <b>Card types…</b>.</p>" +
      "<p>Want to look around first? Open the workspace menu (click the Tabwheel name, top left) and choose <b>Load sample data</b>. <b>Clear sample data</b> removes it again.</p>" +
      "<p class=\"tip\">Tabwheel is invite-only for now. If you sign in and see “You're on the way in”, you're signed in but not let in yet. <a href=\"/welcome#join\">Join the waiting list</a> and we'll email you.</p>"
  },
  {
    id: "wheel",
    code: "WHL",
    title: "The wheel",
    keywords: "spin turn rotate scroll drag flick arrow keys rolodex tab letter a-z back forward open close",
    body:
      "<p>The wheel shows your cards one behind the other, with their tabs peeking over the top. The card at the front is the one you're on.</p>" +
      "<ul>" +
        "<li><b>Turn it:</b> scroll, drag, or use the <kbd>Up</kbd> and <kbd>Down</kbd> arrow keys. It goes all the way round.</li>" +
        "<li><b>Jump:</b> click any tab behind the front card, or a letter in the A–Z strip. <kbd>Home</kbd> and <kbd>End</kbd> go to the first and last card. <kbd>Page Up</kbd> and <kbd>Page Down</kbd> jump ten.</li>" +
        "<li><b>Open a card:</b> click the front card, or press <kbd>Enter</kbd>. It fills the window so there's room to write.</li>" +
        "<li><b>Close it:</b> click outside the card or press <kbd>Esc</kbd>.</li>" +
        "<li><b>Go back:</b> <kbd>Alt</kbd> + <kbd>Left</kbd> and <kbd>Alt</kbd> + <kbd>Right</kbd> arrow move back and forward through the cards you've visited.</li>" +
      "</ul>" +
      "<p>If your device is set to reduce motion, the wheel jumps straight to each card instead of turning.</p>"
  },
  {
    id: "cards",
    code: "CRD",
    title: "Cards and card types",
    keywords: "card type custom fields status colour color tab style paper link favourite star tag checklist archive trash delete undo",
    body:
      "<p>Every card has a type, such as Customer, Job or Meeting. The type decides the card's fields, its statuses and its colour.</p>" +
      "<ul>" +
        "<li><b>Your own types:</b> workspace menu → <b>Card types…</b>. Give it a name, add the fields you need, then choose statuses and a colour. Drag types to change their order.</li>" +
        "<li><b>Style:</b> a type's colour, tab label, tab position, paper (plain, lined or grid) and front-of-card fields apply to every card of that type.</li>" +
        "<li><b>Links:</b> link related cards, such as a customer and their jobs. Click a link to jump there, then use Back to return.</li>" +
        "<li><b>Favourites:</b> star a card to pin it to Favourites and the Today page.</li>" +
        "<li><b>Tags and checklists:</b> add <code>#tags</code> to group cards across types. A checklist on a project sets its progress.</li>" +
        "<li><b>Archive:</b> hides a finished card from every view. Search still finds it.</li>" +
        "<li><b>Delete:</b> moves the card to Trash for 30 days. Click <b>Undo</b> straight away, or restore it from <b>Trash</b>.</li>" +
      "</ul>"
  },
  {
    id: "search",
    code: "SRC",
    title: "Search",
    keywords: "find search filter query is: has: type: tag overdue stale reminder repeating quick filters shortcut slash",
    body:
      "<p>Press <kbd>/</kbd> (or <kbd>Ctrl</kbd> + <kbd>K</kbd>, <kbd>⌘</kbd> + <kbd>K</kbd> on a Mac) and start typing. Search reads names, fields, notes, tags, links and history. Pick a result and the wheel spins to it.</p>" +
      "<p>Click into the empty search box to see quick filters, or type these yourself:</p>" +
      "<ul>" +
        "<li><code>#boiler</code>: cards with that tag</li>" +
        "<li><code>type:quote</code>: one card type</li>" +
        "<li><code>is:open</code>: cards with that status. <code>is:fav</code>: your favourites</li>" +
        "<li><code>is:overdue</code>, <code>is:today</code>, <code>is:tomorrow</code>, <code>is:week</code>, <code>is:nextweek</code>: by date</li>" +
        "<li><code>is:stale</code>: open work nobody has touched for two weeks (“gone quiet”)</li>" +
        "<li><code>is:reminder</code>: cards with a reminder set</li>" +
        "<li><code>is:repeating</code>: cards that repeat</li>" +
        "<li><code>has:notes</code>, <code>has:file</code>, <code>has:link</code>, <code>has:checklist</code></li>" +
      "</ul>" +
      "<p>Mix them with words: <code>is:overdue #boiler</code> finds overdue boiler work.</p>"
  },
  {
    id: "calendar",
    code: "CAL",
    title: "Calendar",
    keywords: "calendar month week add double-click drag reschedule move day date meeting times",
    body:
      "<p>The <b>Calendar</b> view shows every card with a date. Meeting chips show their start time.</p>" +
      "<ul>" +
        "<li><b>Add:</b> click <b>+ Add</b> under the calendar, or double-click a day, then pick what to add. The date is filled in for you.</li>" +
        "<li><b>Move:</b> drag a chip to another day. You'll see an <b>Undo</b> button in case it was a slip.</li>" +
        "<li><b>Week view:</b> switch between <b>Month</b> and <b>Week</b> at the top. Tabwheel remembers your choice.</li>" +
      "</ul>" +
      "<p class=\"tip\">Dragging needs a mouse for now. On a phone, open the card and change its date instead. A repeating meeting can't be dragged to one day: open it to change the series.</p>"
  },
  {
    id: "reminders",
    code: "REM",
    title: "Reminders and snooze",
    keywords: "reminder remind me notification alert snooze later tomorrow next week bell done",
    body:
      "<p>Open any card and click <b>Remind me</b>. Choose later today, tomorrow at 9:00, next Monday at 9:00, or a time of your own. <b>Clear</b> removes it.</p>" +
      "<p>When it's due, a bar pops up with <b>Open</b>, <b>Snooze</b> and <b>Done</b>. If you allow notifications, you'll get one from your browser too. Cards with a reminder show a small bell, and the Today page lists them.</p>" +
      "<p><b>Snooze</b> asks when: tomorrow, in 3 days, next Monday, next week, or a date you pick. Every snooze has an Undo.</p>" +
      "<p class=\"tip\">Reminders only go off while Tabwheel is open in a browser tab. Email reminders are planned.</p>"
  },
  {
    id: "repeating",
    code: "RPT",
    title: "Repeating tasks",
    keywords: "repeat repeating recurring every week month year until times service renewal done next date",
    body:
      "<p>Some jobs come round again: a boiler service, an insurance renewal, monthly invoicing. Set a card with a date to repeat, and choose how often.</p>" +
      "<ul>" +
        "<li>When you tick it <b>Done</b>, the card moves on to its next date. There's no copy to tidy up.</li>" +
        "<li>To stop it, set an <b>until</b> date, or a <b>number of times</b>. After the last one it's simply done.</li>" +
        "<li>Search <code>is:repeating</code> to see everything that repeats.</li>" +
      "</ul>" +
      "<p>Meetings repeat in the same way, and each meeting date gets its own page of notes.</p>"
  },
  {
    id: "starter-sets",
    code: "SET",
    title: "Starter sets",
    keywords: "starter set pack chooser trades office shop property it infrastructure blank card types manager add",
    body:
      "<p>A starter set gives a new workspace matching card types and a few example cards, so it's useful straight away instead of empty.</p>" +
      "<ul>" +
        "<li><b>Trades &amp; services:</b> Customer, Job, Quote, Invoice, Supplier, Van &amp; kit.</li>" +
        "<li><b>Office &amp; agency:</b> Client, Project, Task, Meeting, Invoice.</li>" +
        "<li><b>Shop &amp; hospitality:</b> Stock, Order, Booking, Check, Supplier.</li>" +
        "<li><b>Property &amp; lettings:</b> Property, Tenancy, Certificate, Repair, Viewing, Landlord.</li>" +
        "<li><b>IT &amp; infrastructure:</b> Server, Storage, Issue, Person, Meeting, Project — the original set.</li>" +
      "</ul>" +
      "<p>Built-in types the set doesn't need are hidden from + New, the sidebar and the calendar (they come back on their own if you ever put a card on one). Add another set later, or turn a hidden type back on, from the workspace menu → <b>Card types…</b>.</p>"
  },
  {
    id: "templates",
    code: "TPL",
    title: "Templates and duplicate",
    keywords: "template duplicate copy save as template job sheet new from template",
    body:
      "<p>On an open card, <b>Duplicate</b> makes a copy and <b>Save as template</b> keeps it as a starting point.</p>" +
      "<ul>" +
        "<li>Templates appear under their type in <b>+ New card</b>.</li>" +
        "<li>Copies start fresh: the checklist is unticked, and there's no history, reminder or favourite.</li>" +
        "<li>Manage templates from the workspace menu → <b>Templates…</b>.</li>" +
      "</ul>" +
      "<p>Templates stay out of the wheel, search and the calendar, so they never get in the way.</p>"
  },
  {
    id: "notes",
    code: "NTS",
    title: "Notes pages and files",
    keywords: "notes page write paste screenshot photo image pdf file drop drag resize formatting meeting notes",
    body:
      "<p>Each card has a notes page. Click anywhere on it to start writing there.</p>" +
      "<ul>" +
        "<li><b>Photos and screenshots:</b> paste with <kbd>Ctrl</kbd> + <kbd>V</kbd> (<kbd>⌘</kbd> + <kbd>V</kbd> on a Mac), or drop a file. It lands where you last clicked.</li>" +
        "<li><b>Move and resize:</b> drag the dotted handle to move a block, and its edge to resize it.</li>" +
        "<li><b>Formatting:</b> select text for the toolbar. Start a line with <code>- </code> for a list or <code>[] </code> for a to-do.</li>" +
        "<li><b>Meetings:</b> a meeting card has a fresh page for each date, so one card builds up the whole history.</li>" +
      "</ul>" +
      "<p>Files are private to your workspace. A copied image link stops working after a day.</p>" +
      "<p class=\"tip\">Please don't keep passwords in notes. Link to your password manager instead.</p>"
  },
  {
    id: "folders",
    code: "FLD",
    title: "Folders",
    keywords: "folder subfolder organise file move drag sidebar place client",
    body:
      "<p>Folders sit in the sidebar. Use them for places, clients or projects, and use card types for kinds of thing.</p>" +
      "<ul>" +
        "<li>Drag a card from the list onto a folder to file it, or pick the folder on the open card.</li>" +
        "<li>Folders can hold other folders. A card belongs to one folder at a time.</li>" +
        "<li>Click a folder to show just its cards on the wheel.</li>" +
      "</ul>"
  },
  {
    id: "work-home",
    code: "W/H",
    title: "Work and Home",
    keywords: "work home personal space switch toggle separate",
    body:
      "<p>The <b>Work/Home</b> switch at the top keeps your personal cards separate from the business. Each side has its own cards, folders and card types.</p>" +
      "<p>The first time you switch to Home, Tabwheel adds some starter types: Contacts, Home, Car, Insurance and policies, Documents and Reminders. Work gets its own starter-set chooser the first time there's nothing in it (see <b>Starter sets</b>). Tabwheel remembers which side you were on.</p>"
  },
  {
    id: "import",
    code: "IMP",
    title: "Importing",
    keywords: "import spreadsheet csv excel xlsx contacts vcard vcf calendar ics outlook set",
    body:
      "<p>Workspace menu → <b>Import spreadsheets, contacts or calendars…</b>. You'll see a preview before anything is added.</p>" +
      "<ul>" +
        "<li><b>Spreadsheets</b> (CSV or Excel): each row becomes a card. Match the columns to fields, or import it as <b>a new set</b> with its own card type and folder.</li>" +
        "<li><b>Contacts</b> (.vcf): each contact becomes a person card.</li>" +
        "<li><b>Calendars</b> (.ics, for example from Outlook: File › Save Calendar): each meeting becomes a card with its Join link. Importing again updates them rather than adding copies.</li>" +
      "</ul>"
  },
  {
    id: "backups",
    code: "BAK",
    title: "Backups and restore",
    keywords: "backup export restore download json safety nightly lost recover trash",
    body:
      "<p>Your workspace is backed up every night, and each copy is kept for 30 days. That copy covers cards, notes, card types and folders, but not uploaded photos or PDFs.</p>" +
      "<p>You can make your own backup any time: workspace menu → <b>Export a backup</b>. The menu reminds you in amber if it's been more than two weeks.</p>" +
      "<p>To bring one back: workspace menu → <b>Restore from a backup…</b>. Tabwheel shows what's in the file and asks before it changes anything.</p>" +
      "<p class=\"tip\">Deleted a card by mistake? Look in <b>Trash</b> first. Cards stay there for 30 days.</p>"
  },
  {
    id: "storage",
    code: "LIM",
    title: "Storage and limits",
    keywords: "storage limit space size full quota gb mb files database usage",
    body:
      "<p>Tabwheel doesn't limit how many cards, notes or files you have. The limits come from the service underneath.</p>" +
      "<ul>" +
        "<li><b>Files:</b> 1 GB of photos, screenshots and PDFs per workspace during early access.</li>" +
        "<li><b>Cards and notes:</b> text is tiny, so there's room for a very large number.</li>" +
      "</ul>" +
      "<p>To see how much you're using, open the workspace menu and look under <b>Data &amp; safety</b>. The bars turn red when you're close to a limit.</p>"
  },
  {
    id: "privacy",
    code: "PRV",
    title: "Privacy and your data",
    keywords: "privacy data gdpr where stored eu ireland supabase google sign in delete account export trackers",
    body:
      "<p>Your cards and files are stored with Supabase in the EU (Ireland). Only people in your workspace can see them. You sign in with Google, so Tabwheel never sees your password.</p>" +
      "<ul>" +
        "<li>We don't sell your data, show ads or use tracking scripts.</li>" +
        "<li>You can export everything at any time.</li>" +
        "<li>To have your account and workspace deleted, email us.</li>" +
      "</ul>" +
      "<p>The full details are in the <a href=\"/privacy\">privacy notice</a>.</p>"
  }
];

/* plain text of a topic, for searching (and for anything that can't render HTML) */
export function topicText(t){
  return (t.title + " " + t.keywords + " " + String(t.body).replace(/<[^>]+>/g, " ").replace(/&amp;/g, "&").replace(/&[a-z]+;/g, " ")).replace(/\s+/g, " ");
}

/* topics matching a free-text query: every word must appear somewhere, title hits first */
export function searchTopics(query, topics){
  topics = topics || HELP_TOPICS;
  var words = String(query || "").toLowerCase().split(/\s+/).filter(Boolean);
  if(!words.length) return topics.slice();
  return topics.map(function(t, i){
    var hay = topicText(t).toLowerCase(), title = t.title.toLowerCase(), score = 0;
    for(var w = 0; w < words.length; w++){
      if(hay.indexOf(words[w]) < 0) return null;
      if(title.indexOf(words[w]) >= 0) score += 3;
      if(t.keywords.toLowerCase().indexOf(words[w]) >= 0) score += 1;
    }
    return {t: t, s: score, i: i};
  }).filter(Boolean).sort(function(a, b){ return b.s - a.s || a.i - b.i; }).map(function(x){ return x.t; });
}
